/**
 *
 * @filename src/pages/analytics/Components/PublicDrawers/utils.ts
 * @author  tujiawei <jiaweitu@marchthepolo.com>
 * @date 2024-11-12 20:01:52
 */

import { groupBy, padStart } from 'lodash-es'

const dot = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:#5470c6;"></span>`

const line = (key: string, value: string) => {
	return `
		<div style="margin: 10px 0 0;line-height:1;">
			${dot}
			<span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${key}</span>
			<span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">
        ${value}
			</span>
			<div style="clear:both"></div>
		</div>
  `
}
export const itemFormatter = (entries: string[][]) => {
	return `
  	<div>
      ${entries.map(entry => line(entry[0], entry[1])).join('')}
    </div>
  `
}

export const data = [
	{
		id: 804,
		operator_id: 5561,
		date: '2024-11-07',
		online_seconds: 51743,
		created_at: '2024-11-08 00:01:01',
		operator_name: 'Ian',
		start_time: '2024-11-08 00:01:01',
		end_time: '2024-11-08 14:23:24'
	},
	{
		id: 806,
		operator_id: 5561,
		date: '2024-11-08',
		online_seconds: 37314,
		created_at: '2024-11-08 10:20:27',
		operator_name: 'Ian',
		start_time: '2024-11-08 10:20:27',
		end_time: '2024-11-08 20:42:21'
	},
	{
		id: 836,
		operator_id: 5561,
		date: '2024-11-10',
		online_seconds: 3600,
		created_at: '2024-11-10 06:59:19',
		operator_name: 'Ian',
		start_time: '2024-11-10 06:59:19',
		end_time: '2024-11-10 07:59:19'
	},
	{
		id: 837,
		operator_id: 5561,
		date: '2024-11-10',
		online_seconds: 3600,
		created_at: '2024-11-10 09:00:00',
		operator_name: 'Ian',
		start_time: '2024-11-10 09:00:00',
		end_time: '2024-11-10 10:00:00'
	},
	{
		id: 838,
		operator_id: 5561,
		date: '2024-11-10',
		online_seconds: 7200,
		created_at: '2024-11-10 11:11:11',
		operator_name: 'Ian',
		start_time: '2024-11-10 11:11:11',
		end_time: '2024-11-10 13:11:11'
	},
	{
		id: 828,
		operator_id: 5561,
		date: '2024-11-11',
		online_seconds: 2379,
		created_at: '2024-11-11 11:45:43',
		operator_name: 'Ian',
		start_time: '2024-11-11 11:45:43',
		end_time: '2024-11-11 12:25:22'
	},
	{
		id: 828,
		operator_id: 5562,
		date: '2024-11-11',
		online_seconds: 2379,
		created_at: '2024-11-11 11:45:43',
		operator_name: 'Ian2',
		start_time: '2024-11-11 11:45:43',
		end_time: '2024-11-11 12:25:22'
	}
].concat(
	Array.from({ length: 100 }, (_, i) => ({
		id: 828,
		operator_id: 5562 + 100 + i,
		date: `2024-11-${11 + i}`,
		online_seconds: 2379,
		created_at: '2024-11-11 11:45:43',
		operator_name: 'Ian2',
		start_time: '2024-11-11 11:45:43',
		end_time: '2024-11-11 12:25:22'
	}))
)

const formatDuration = (seconds: number) => {
	const second = seconds % 60
	const minute = ((seconds - second) / 60) % 60
	const hour = ((seconds - minute * 60 - second) / 60 / 60) % 60

	return [hour, minute, second].map(value => padStart(`${value}`, 2, '0')).join(':')
}

// 需要统一到同一天
const year = 2000
const month = 0
const date = 1

const normalizeDate = (dateStr: string) => {
	const [hour, minute, second] = dateStr
		.split(' ')[1]
		.split(':')
		.map(item => Number(item))
	return new Date(year, month, date, hour, minute, second).getTime()
}

export const min = new Date(year, month, date)
export const max = new Date(year, month, date + 1)

export const formatData = (data: Record<string, any>[]) => {
	const res = groupBy(data, 'date')
	const entries = Object.keys(res)
		.sort()
		.map(date => {
			return [date, groupBy(res[date], 'operator_id')]
		})

	const yLabels: string[][] = []

	entries.forEach(entry => {
		Object.values(entry[1]).forEach(item => {
			const value = item[0] || {}
			yLabels.push([`${value.operator_name}\n\n${value.date}`])
		})
	})

	const indexMap = new Map<string, number>()
	yLabels.forEach(([key], index) => {
		indexMap.set(key, index)
	})

	const values = data.map(item => {
		const key = `${item.operator_name}\n\n${item.date}`
		const index = indexMap.get(key)

		const DAY_SECONDS = 60 * 60 * 24

		let startTime = item.start_time
		let endTime = item.end_time
		if (item.online_seconds >= DAY_SECONDS) {
			startTime = `${item.date} 00:00:00`

			// 结束时间需要再调整
			item.end_time = endTime = `${item.date} 24:00:00`
		}

		if (endTime > `${item.date} 23:59:59`) {
			item.end_time = endTime = `${item.date} 23:59:59`
		}

		const start = normalizeDate(item.start_time)
		const end = normalizeDate(item.end_time)

		return [
			index,
			item.operator_name,
			start,
			end,
			formatDuration(Math.min(item.online_seconds, DAY_SECONDS)),
			startTime,
			endTime
		]
	})

	return {
		yLabels,
		values
	}
}
